import { routeFactory } from "@/RouteFactory";
import { Crumb } from "@/components/bread-crumbs/Crumb";
import { PageHeader } from "@/components/page-header/PageHeader";
import { Box, Row, Space, Txt } from "@stenajs-webui/core";
import {
  BreadCrumbs,
  FlatButton,
  ResultListBanner,
  stenaBusinessAgreement,
  useResultListBannerState,
} from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import * as React from "react";
import { HeadAgreementActionButtons } from "../../../head-agreement/details/components/HeadAgreementActionButtons";
import { CustomerNegotiationStatusEditorFetcher } from "../../customer-negotiation-status/components/CustomerNegotiationStatusEditorFetcher";
import { NoExistingHeadAgreementActionButtons } from "../no-existing-head-agreement-action-buttons/NoExistingHeadAgreementActionButtons";
import { useCustomer } from "../../../../../hooks/UseCustomer";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { HeadAgreementPanelQuery } from "@/gql/graphql";

interface Props {
  customerId: string;
  negotiationYear?: number;
  headAgreement?:
    | HeadAgreementPanelQuery["productPrice"]["headAgreement"]["byId"]
    | null;
  loading?: boolean;
}

const border = `1px solid ${cssColor("--lhds-color-ui-500")}`;

export const CustomerHeadAgreementHeader: React.FC<Props> = React.memo(
  ({ customerId, headAgreement, loading, negotiationYear }) => {
    const currentYear = new Date().getUTCFullYear();
    const { loading: loadingCustomer, customer } = useCustomer(
      customerId,
      negotiationYear ?? currentYear
    );

    const resultListBanner = useResultListBannerState(undefined);

    return (
      <PageHeader
        icon={stenaBusinessAgreement}
        title={"Header agreement"}
        loading={loading || loadingCustomer}
        contentUnder={
          resultListBanner.bannerState && (
            <ResultListBanner
              bannerState={resultListBanner.bannerState}
              variant={"error"}
              contentRight={
                <FlatButton
                  label={"Close"}
                  onClick={resultListBanner.clearBannerResult}
                />
              }
            />
          )
        }
        breadcrumbs={
          <BreadCrumbs>
            <Crumb
              path={routeFactory.productAndPrice.customer.customer({
                customerId,
              })}
              label={customer?.breadCrumbName ?? ""}
            />
            <Crumb label={String(headAgreement?.headAgreementNumber ?? "")} />
          </BreadCrumbs>
        }
        contentRight={
          headAgreement?.id ? (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              data-testid={testIdConstants.headerAgreementNegotiationStatus}
            >
              <Txt color={cssColor("--lhds-color-ui-600")}>
                Neg. status({negotiationYear})
              </Txt>
              <Space num={2} />
              <Row width={"160px"} borderRadius={"4px"} height={"32px"}>
                <CustomerNegotiationStatusEditorFetcher
                  customerId={customerId}
                  negotiationYear={negotiationYear ?? currentYear}
                  borderRadius={"4px"}
                  border={border}
                />
              </Row>
              <Space num={2} />
              <HeadAgreementActionButtons
                customer={customer}
                headAgreement={headAgreement}
                resultListBanner={resultListBanner}
              />
            </Box>
          ) : customer?.parent ? (
            <NoExistingHeadAgreementActionButtons
              parentCustomerId={customer.parent.id}
            />
          ) : null
        }
      />
    );
  }
);
